import * as T from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DeerBehavior, type DeerHabitat, type DeerPlayer, type DeerState } from './deerBehavior';
import { DeerEyes, type DeerExpression } from './deerEyes';
import { composeEyeLook } from '../character/eyeLook';
import { DeerGrounding } from './deerGrounding';

type LookJoint = { bone: T.Object3D; authored: T.Quaternion; up: T.Vector3; right: T.Vector3; weight: number };
export interface DeerPose {
  x:number;z:number;yaw:number;speed:number;state:DeerState;elapsed:number;fear:number;
  distance:number;time:number;lookAtPlayer:boolean;scale:number;
}

/** A reusable world actor. The host owns terrain and player input; the deer owns
 * its behavior, gait blending, face, head tracking and planar navigation. */
export class ForestDeer {
  readonly group = new T.Group();
  readonly brain: DeerBehavior;
  readonly eyes: DeerEyes;
  readonly mixer: T.AnimationMixer;
  readonly actions = new Map<string, T.AnimationAction>();
  readonly model: T.Group;
  readonly grounding: DeerGrounding;
  expressionOverride: DeerExpression | null = null;
  private active: T.AnimationAction | null = null;
  private lookJoints: LookJoint[] = [];
  private yaw = 0; private pitch = 0;
  private q = new T.Quaternion(); private scratch = new T.Quaternion();

  constructor(model: T.Group, clips: T.AnimationClip[], habitat: DeerHabitat,
    readonly groundHeight: (x: number, z: number) => number, private readonly ownsResources = true) {
    this.model = model; this.brain = new DeerBehavior(habitat);
    this.group.name = 'Forest deer'; this.group.add(model); model.scale.setScalar(this.brain.scale);
    this.eyes = new DeerEyes(model);
    this.grounding = new DeerGrounding(model, this.group, groundHeight);
    model.traverse(o => { if (o instanceof T.Mesh) { o.castShadow = true; o.receiveShadow = true; o.frustumCulled = false; } });
    model.updateMatrixWorld(true);
    for (const [name, weight] of [['Neck', .24], ['NeckUpper', .30], ['Head', .46]] as const) {
      const bone = model.getObjectByName(name)!;
      const inv = bone.getWorldQuaternion(new T.Quaternion()).invert();
      this.lookJoints.push({ bone, weight, authored: bone.quaternion.clone(),
        up: new T.Vector3(0, 1, 0).applyQuaternion(inv), right: new T.Vector3(-1, 0, 0).applyQuaternion(inv) });
    }
    this.mixer = new T.AnimationMixer(model);
    for (const clip of clips) this.actions.set(clip.name, this.mixer.clipAction(clip));
    this.play('Idle', 0); this.update(0, { x: 0, z: 8, speed: 0 });
  }

  static async load(habitat: DeerHabitat, groundHeight: (x: number, z: number) => number): Promise<ForestDeer> {
    const gltf = await new GLTFLoader().loadAsync('/models/creatures/forest-deer.glb');
    return new ForestDeer(gltf.scene, gltf.animations, habitat, groundHeight);
  }
  get clip(): string { return this.active?.getClip().name ?? ''; }
  setMode(mode: DeerState | 'auto'): void { this.brain.setMode(mode); }
  reset(): void {
    this.grounding.reset();
    this.brain.reset(); this.yaw = 0; this.pitch = 0;
    this.mixer.stopAllAction(); this.active = null; this.play('Idle', 0);
    this.update(0, { x: 0, z: 8, speed: 0 });
  }
  private play(name: string, fade = .5): void {
    const action = this.actions.get(name);
    if (!action) throw new Error(`Missing deer animation ${name}`);
    if (action === this.active) return;
    this.active?.fadeOut(fade);
    action.reset().setEffectiveWeight(1).setEffectiveTimeScale(1);
    action.setLoop(name === 'Alert' ? T.LoopOnce : T.LoopRepeat, name === 'Alert' ? 1 : Infinity);
    action.clampWhenFinished = true;
    action.fadeIn(fade).play(); this.active = action;
  }
  update(dt: number, player: DeerPlayer): void {
    dt = T.MathUtils.clamp(dt, 0, .1);
    this.brain.update(dt, player);
    this.updatePose(dt,this.brain,player);
  }
  /** A server-driven actor supplies its pose without advancing the playground
   * brain. Solve in the habitat frame, then attach that frame to the planet. */
  updatePose(dt:number,b:DeerPose,player:DeerPlayer):void {
    dt=T.MathUtils.clamp(dt,0,.1);
    this.group.position.set(b.x, this.groundHeight(b.x, b.z), b.z); this.group.rotation.y = b.yaw;
    const clip = b.state === 'walk' && b.speed >= .05 ? 'Walk_InPlace' : b.state === 'graze' ? 'Graze' : b.state === 'flee' ? 'Run_InPlace' : b.state === 'alert' && b.elapsed < 3.2 ? 'Alert' : 'Idle';
    this.play(clip, b.state === 'flee' ? .3 : .55);
    if (this.active && (b.state === 'walk' || b.state === 'flee')) {
      const gaitSpeed = (b.state === 'walk' ? .37109375 : 1.98275862) * b.scale;
      this.active.setEffectiveTimeScale(Math.max(.12, b.speed / gaitSpeed));
    }
    // Restore the previous authored result before mixing, so additive tracking
    // never accumulates when a constant animation channel skips a redundant write.
    for (const joint of this.lookJoints) joint.bone.quaternion.copy(joint.authored);
    this.grounding.restore();
    this.mixer.update(dt);
    for (const joint of this.lookJoints) joint.authored.copy(joint.bone.quaternion);
    this.grounding.update(dt, { yaw: b.yaw, speed: b.speed, clip, time: this.active?.time ?? 0 });
    const toward = Math.atan2(player.x - b.x, player.z - b.z) - b.yaw;
    const bearing = Math.atan2(Math.sin(toward), Math.cos(toward));
    // Let the initial alert check play, then settle into sustained player tracking.
    const track = b.lookAtPlayer ? T.MathUtils.smoothstep(b.elapsed, .8, 2.7) : 0;
    this.yaw = T.MathUtils.damp(this.yaw, T.MathUtils.clamp(bearing, -.85, .85) * track, 7, dt);
    this.pitch = T.MathUtils.damp(this.pitch, Math.atan2(1.5 - 1.13 * b.scale, Math.max(.5, b.distance)) * track, 7, dt);
    for (const joint of this.lookJoints) {
      composeEyeLook(this.q, this.scratch, joint.up, joint.right, -this.yaw * joint.weight, this.pitch * joint.weight);
      joint.bone.quaternion.multiply(this.q);
    }
    const expression: DeerExpression = this.expressionOverride ?? (b.state === 'flee' ? 'worried' : b.state === 'alert' ? b.fear > .25 ? 'alert' : 'curious' : 'calm');
    this.eyes.setExpression(expression);
    this.eyes.setGaze(b.lookAtPlayer ? -T.MathUtils.clamp(bearing - this.yaw, -.24, .24) : .035 * Math.sin(b.time * 1.6), b.state === 'graze' ? -.08 : this.pitch * .25);
    this.eyes.update(dt);
  }
  dispose(): void {
    this.mixer.stopAllAction(); this.mixer.uncacheRoot(this.model);
    this.group.removeFromParent();
    const skeletons = new Set<T.Skeleton>();
    this.model.traverse(o=>{if(o instanceof T.SkinnedMesh)skeletons.add(o.skeleton);});
    for(const skeleton of skeletons)skeleton.dispose();
    if(this.ownsResources)this.model.traverse(o => { if (o instanceof T.Mesh) {
      o.geometry.dispose();
      for (const material of Array.isArray(o.material) ? o.material : [o.material]) material.dispose();
    } });
  }
}
